import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { MouseEventParams } from 'lightweight-charts'
import ComparePlusIcon from '../../../assets/icons/complarePlus'
import { SpecKeys } from '../../KeyboardShortcuts/Shortcuts.types'
import { useSelector } from 'react-redux'
import { selectIsEmbedded } from '../../../selectors/device'
import { selectChartConfig } from '../../../selectors/chart'
import { callNative } from '../../../util/nativeBridge'
import { isMacOS } from '../../../util/general'
import { AppContext } from '../../../App'
import Chart from '../chart'
import { getChartHoverActive } from '../chartHoverState'
import { ChartContextMenuAnchor } from '../chartContextMenuOptions/ChartContextMenuHost'
import './chartContextMenuButton.css'

interface Props {
    onOpenContextMenu: (anchor: ChartContextMenuAnchor) => void
    paneContainer: HTMLDivElement
    paneIndex: number
    viewKey: string
}

const PLUS_BUTTON_WIDTH = 21
const PLUS_BUTTON_EDGE_OFFSET = 22

const getPlusButtonLeft = (timeScaleWidth: number, scaleOnRight: boolean) =>
    scaleOnRight ? timeScaleWidth - PLUS_BUTTON_EDGE_OFFSET : 0

const getPlusButtonCrosshairX = (timeScaleWidth: number, scaleOnRight: boolean) =>
    scaleOnRight ? timeScaleWidth - 10 : 10

const ChartContextMenuButton = ({ onOpenContextMenu, paneContainer, paneIndex, viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chartRef = useRef<Chart | null>(null)
    const btnRef = useRef<HTMLButtonElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const containerRect = useRef<DOMRect>(undefined)
    const currentPaneIndex = useRef<number>(undefined)
    const isPlusButtonHoveredRef = useRef(false)
    const [plusShortcutEnabled, setPlusShortcutEnabled] = useState(false)
    const [chartReady, setChartReady] = useState(false)
    const isEmbedded = useSelector(selectIsEmbedded)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const scaleOnRight = useMemo(() => {
        const opts = chartConfig?.scaleOptions
        if (opts?.leftScaleVisible && !opts?.rightScaleVisible) return false
        if (!opts?.leftScaleVisible && opts?.rightScaleVisible) return true
        return chartRef.current?.getActivePriceScaleSide() !== 'LEFT'
    }, [chartConfig?.scaleOptions, chartReady])

    useEffect(() => {
        let cancelled = false
        const attachChart = () => {
            if (cancelled) return
            const chart = appContext.getChart(viewKey)
            if (chart?.chartApi) {
                chartRef.current = chart
                setChartReady(true)
                return
            }
            requestAnimationFrame(attachChart)
        }
        setChartReady(false)
        chartRef.current = null
        attachChart()
        return () => {
            cancelled = true
        }
    }, [appContext, viewKey])

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const detectKey = isMacOS() ? SpecKeys.META : SpecKeys.CONTROL
        if (e.key === detectKey && e.altKey) {
            setPlusShortcutEnabled(true)
        } else {
            setPlusShortcutEnabled(false)
        }
    }, [])

    const handleKeyUp = useCallback(() => {
        setPlusShortcutEnabled(false)
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [handleKeyDown, handleKeyUp])

    const crossHairSubscription = useCallback((a: MouseEventParams) => {
        if (a.paneIndex !== undefined) currentPaneIndex.current = a.paneIndex
        if (!a.point) {
            if (btnRef.current && !isPlusButtonHoveredRef.current) btnRef.current.style.opacity = '0'
            return
        }
        if (btnRef.current) {
            if (currentPaneIndex.current === paneIndex && getChartHoverActive(viewKey)) {
                try {
                    btnRef.current.style.opacity = '1'
                    if (a.point?.y) {
                        btnRef.current.style.top = ((a.point.y) - 10.5) + 'px'
                    }
                    if (plusShortcutEnabled) {
                        if (a.point?.x) {
                            const timeScale = chartRef.current?.chartApi?.timeScale()
                            if (timeScale) {
                                const nearestTime = timeScale.coordinateToTime(a.point.x)
                                if (nearestTime) {
                                    const nearestXposition = timeScale.timeToCoordinate(nearestTime)
                                    btnRef.current.style.left = ((nearestXposition ?? 0) - 10.5) + 'px'
                                }
                            }
                        }
                    } else {
                        const timeScaleWidth = chartRef.current?.chartApi?.timeScale().width() ?? 0
                        btnRef.current.style.left = `${getPlusButtonLeft(timeScaleWidth, scaleOnRight)}px`
                    }
                } catch {
                    btnRef.current.style.opacity = '0'
                }
            } else {
                btnRef.current.style.opacity = '0'
            }
        }
    }, [paneIndex, viewKey, plusShortcutEnabled, scaleOnRight])

    useEffect(() => {
        const chart = chartRef.current
        if (!chart?.chartApi) return
        chart.chartApi.subscribeCrosshairMove(crossHairSubscription)
        return () => {
            chart.chartApi?.unsubscribeCrosshairMove(crossHairSubscription)
        }
    }, [crossHairSubscription, chartReady])

    const handlePlusClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const chart = chartRef.current
        if (!chart) return
        const y = Number(btnRef.current?.style.top.slice(0, -2)) + 10.5
        const ctx = chart.getContextMenuSeriesContext(paneIndex)
        let price = ''
        if (ctx) {
            price = ctx.series.coordinateToPrice(y)?.toFixed(2) ?? ''
        }
        const btnRect = btnRef.current?.getBoundingClientRect()
        const anchorX = btnRect ? (scaleOnRight ? btnRect.left : btnRect.right) : e.clientX
        const anchorY = btnRect?.top ?? e.clientY
        if (isEmbedded) {
            callNative('onChartContextMenuClicked', price)
        } else {
            onOpenContextMenu({
                x: anchorX,
                y: anchorY,
                price,
                position: 'right',
                anchorOriginHorizontal: scaleOnRight ? 'left' : 'right',
                transformOriginHorizontal: scaleOnRight ? 'right' : 'left',
                seriesGroup: ctx?.seriesGroupConfig
            })
        }
    }, [onOpenContextMenu, isEmbedded, paneIndex, scaleOnRight])

    const handleBtnMouseMove = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const chart = chartRef.current
        if (!getChartHoverActive(viewKey)) return
        const newYposition = e.clientY - (containerRect.current?.y ?? 0) - 10.5
        const newXposition = e.clientX - (containerRect.current?.x ?? 0)
        const paneSeries = chart?.getContextMenuSeriesContext(paneIndex)?.series
        if (!chart?.chartApi || !paneSeries) return
        try {
            const timeScale = chart.chartApi.timeScale()
            const timeScaleWidth = chart.chartApi.timeScale().width() ?? 0
            const time = plusShortcutEnabled
                ? timeScale.coordinateToTime(newXposition)
                : timeScale.coordinateToTime(getPlusButtonCrosshairX(timeScaleWidth, scaleOnRight)) ?? timeScale.coordinateToTime(0)
            const price = paneSeries.coordinateToPrice(newYposition + 10.5)
            if (price == null || time == null) return
            chart.chartApi.setCrosshairPosition(price, time, paneSeries)
            if (btnRef.current) {
                btnRef.current.style.opacity = '1'
                btnRef.current.style.top = newYposition + 'px'
                if (time && plusShortcutEnabled) {
                    const nearestXposition = timeScale.timeToCoordinate(time)
                    btnRef.current.style.left = ((nearestXposition ?? 0) - 10.5) + 'px'
                }
            }
        } catch {
            // coordinateToPrice / setCrosshairPosition can throw when chart is updating
        }
    }, [viewKey, plusShortcutEnabled, paneIndex, scaleOnRight])

    const isMovingToPlusButton = useCallback((relatedTarget: EventTarget | null) => {
        return relatedTarget instanceof Node && !!btnRef.current?.contains(relatedTarget)
    }, [])

    const isMovingToChartPane = useCallback((relatedTarget: EventTarget | null) => {
        if (!(relatedTarget instanceof Node)) return false
        const paneElements = chartRef.current?.chartApi?.getPaneElements?.() ?? []
        const paneEl = paneElements[paneIndex]
        return !!paneEl?.contains(relatedTarget)
    }, [paneIndex])

    const handleMouseLeave = useCallback((e?: { relatedTarget: EventTarget | null }) => {
        if (isPlusButtonHoveredRef.current || isMovingToPlusButton(e?.relatedTarget ?? null)) return
        if (btnRef.current) {
            btnRef.current.style.opacity = '0'
        }
        if (chartRef.current?.chartApi) {
            try {
                chartRef.current.chartApi.clearCrosshairPosition()
            } catch {
                // clearCrosshairPosition can throw when chart is updating
            }
        }
    }, [isMovingToPlusButton])

    const handleBtnMouseEnter = useCallback(() => {
        isPlusButtonHoveredRef.current = true
    }, [])

    const handleBtnMouseLeave = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        isPlusButtonHoveredRef.current = false
        if (isMovingToChartPane(e.relatedTarget)) return
        handleMouseLeave()
    }, [handleMouseLeave, isMovingToChartPane])

    useEffect(() => {
        if (containerRef.current) {
            containerRect.current = containerRef.current.getBoundingClientRect()
        }
        const chart = chartRef.current
        const paneElements = chart?.chartApi?.getPaneElements?.() ?? []
        const paneEl = paneElements[paneIndex]
        const canvas = paneEl?.querySelector('canvas')
        if (canvas) {
            const onCanvasMouseLeave = (e: MouseEvent) => handleMouseLeave(e)
            canvas.addEventListener('mouseleave', onCanvasMouseLeave)
            return () => canvas.removeEventListener('mouseleave', onCanvasMouseLeave)
        }
    }, [paneIndex, handleMouseLeave, chartReady])

    const containerBox = useMemo(() => {
        const chart = chartRef.current
        if (!paneContainer || !chartReady || !chart) return null
        const timeScaleWidth = chart.chartApi?.timeScale().width() ?? 0
        return (
            <div
                id={`chart-${viewKey}-pane-${paneIndex}-context-menu-container`}
                style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none'
                }}
                ref={containerRef}
                onMouseLeave={handleMouseLeave}
            >
                <button
                    ref={btnRef}
                    className={`chart-context-menu-plus-btn ${scaleOnRight ? 'chart-context-menu-plus-btn--scale-right' : 'chart-context-menu-plus-btn--scale-left'}`}
                    style={{
                        height: '21.5px',
                        width: `${PLUS_BUTTON_WIDTH}px`,
                        position: 'absolute',
                        left: getPlusButtonLeft(timeScaleWidth, scaleOnRight),
                        zIndex: 99,
                        top: 0,
                        opacity: 0,
                        pointerEvents: 'auto'
                    }}
                    onClick={handlePlusClick}
                    onMouseEnter={handleBtnMouseEnter}
                    onMouseLeave={handleBtnMouseLeave}
                    onMouseMove={handleBtnMouseMove}
                >
                    <ComparePlusIcon />
                </button>
            </div>
        )
    }, [handlePlusClick, handleBtnMouseEnter, handleBtnMouseLeave, handleBtnMouseMove, handleMouseLeave, paneContainer, paneIndex, viewKey, chartReady, scaleOnRight])

    return (
        <>{containerBox}</>
    )
}

export default memo(ChartContextMenuButton)
